import { useState, useEffect } from 'react';
import { PRESET_COLORS } from 'constants/index';
import helpers from 'helpers';

const COLOR_STORAGE_KEY = 'lastUsedColor';
const CUSTOM_COLORS_KEY = 'customColors';

export const useColorService = () => {
  const [lastUsedColor, setLastUsedColor] = useState(null);
  const [customColors, setCustomColors] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(COLOR_STORAGE_KEY);
    if (saved) {
      if (helpers.isValidColor(saved)) {
        setLastUsedColor(saved);
      } else {
        localStorage.removeItem(COLOR_STORAGE_KEY);
      }
    }

    const savedCustomColors = localStorage.getItem(CUSTOM_COLORS_KEY);
    if (savedCustomColors) {
      try {
        const parsed = JSON.parse(savedCustomColors);
        if (Array.isArray(parsed)) {
          setCustomColors(parsed.filter((color) => helpers.isValidColor(color)));
        }
      } catch (e) {
        localStorage.removeItem(CUSTOM_COLORS_KEY);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const updateLastUsedColor = (color, fromPreset = false) => {
    if (helpers.isValidColor(color)) {
      setLastUsedColor(color);
      localStorage.setItem(COLOR_STORAGE_KEY, color);

      if (fromPreset === false) {
        const isFromPreset = isPresetColorAutoDetect(color);

        if (!isFromPreset) {
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }

          const timer = setTimeout(() => {
            updateCustomColor(color);
          }, 300);
          setDebounceTimer(timer);
        }
      }
    }
  };

  const rgbaToHex = (rgba) => {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match) return rgba;

    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
  };

  const normalizeColor = (color) => {
    if (!color) return null;

    const normalized = color.toLowerCase().trim();

    if (normalized.startsWith('rgba')) {
      return rgbaToHex(normalized);
    }

    if (normalized.startsWith('#')) {
      if (normalized.length === 4) {
        return (
          '#' +
          normalized[1] +
          normalized[1] +
          normalized[2] +
          normalized[2] +
          normalized[3] +
          normalized[3]
        );
      }
      return normalized;
    }

    return normalized;
  };

  const isPresetColorAutoDetect = (color) => {
    const normalizedColor = normalizeColor(color);

    const isInPresets = PRESET_COLORS.some(
      (presetColor) => normalizeColor(presetColor) === normalizedColor
    );

    const isCustomColor =
      customColors.length > 0 && normalizeColor(customColors[0]) === normalizedColor;

    return isInPresets || isCustomColor;
  };

  const updateCustomColor = (color) => {
    const normalizedColor = normalizeColor(color);
    const isPresetColor = PRESET_COLORS.some(
      (presetColor) => normalizeColor(presetColor) === normalizedColor
    );

    if (!isPresetColor) {
      setCustomColors([color]);
      localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify([color]));
    }
  };

  const getPresetColors = () => {
    return customColors.length > 0 ? [customColors[0], ...PRESET_COLORS] : PRESET_COLORS;
  };

  const clearCustomColors = () => {
    setCustomColors([]);
    localStorage.removeItem(CUSTOM_COLORS_KEY);
  };

  return {
    lastUsedColor,
    customColors,
    updateLastUsedColor,
    getPresetColors,
    clearCustomColors,
  };
};

export default useColorService;
